import {useState, useEffect, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Film,
  AlertCircle,
  MailWarning,
  X
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
  Item as ItemPanel,
  ItemContent,
  ItemTitle,
  ScrollArea,
  Spinner,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl} from '@/components/shared/CopyableUrl';
import {
  getAutoSelectedLanguageCode,
  getMetaFieldAny,
  DEFAULT_LANGUAGE_FIELD_NAMES,
  DEFAULT_AUDIO_LANGUAGE_FIELD_NAMES,
  COUNTRY_FIELD_NAMES
} from '@/lib/itemMetadata';
import {type Item, ITEM_FLAGS_MAX_IDS, itemsApi} from '@/api/items';
import {type Classification, type CreateClassificationRequest} from '@/api/classifications';
import {NoteScreenshots, discouragesScreenshots} from '@/components/classify/NoteScreenshots';
import {type ConfigItem} from '@/api/config';
import {type Submission} from '@/api/submissions';
import {
  useCreateClassification,
  useExistingSubmissions,
  useP0EscalationConfig,
  useCreateFollowerSweep,
  usePinterestProfileStatus
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {
  isLeaseLost,
  resolvedElsewhereOf,
  type ResolvedElsewhere,
  type ReviewSignals
} from '@/api/moderationQueue';
import {ClassificationTree} from '@/components/classify/ClassificationTree';
import {
  EnrichmentForm,
  ExampleIdsInput,
  exampleIdsLabel,
  MultiPicker,
  LanguagePicker,
  LocationPicker,
  type EnrichmentValues
} from '@/components/classify/EnrichmentForm';
import {type P0LinkedItem} from '@/components/p0alerts/CreateP0AlertDialog';
import {cn} from '@/lib/utils';
import {isPolicyViolationType} from '@/lib/classificationType';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {DEFAULT_AFFILIATE, PLATFORM_ID} from '@/lib/platform';
import {DEFAULT_PINTEREST_LOCALE} from '@/lib/pinterest';
import {type FlagColor} from '@/api/flags';

// The customer-taxonomy risk nodes (config_items, applied from a taxonomy pack) that arm the sweep.
// Mirrors PRI.Shared.Common.TaxonomyRiskNames.SweepTriggers.
const SWEEP_TRIGGER_RISKS = ['CSAM Creator', 'CSAM Distributor', 'Online Distributor'] as const;

// Whether classifying this item arms the follower sweep, i.e. whether to offer the "collect and
// classify followers" prompt. Only the taxonomy risk the analyst classified under counts; the core
// risk they tag at the Enrich step does not. Mirrors JobsController.CreateFollowerSweepJob, which is
// the real gate; a prompt this says yes to and that endpoint refuses would surface as an error in
// the prompt itself.
//
// A type predicate, not a plain boolean: the caller narrows item.author_handle to non-null off
// this return, and that narrowing doesn't survive a boolean-returning helper across the call
// boundary - the assignment `{handle: item.author_handle}` needs the predicate to type-check.
// riskNames are the risk nodes' `name` (what the classification stores), not their `displayName`.
// Takes every risk the submission carries, not just its primary: a submission can be classified
// under a trigger risk second, and arming off the first alone would silently never offer the
// sweep for exactly those. Mirrors JobsController's own gate, which is
// `source.Risks.Any(SweepTriggers.Contains)` over the whole list.
export function armsFollowerSweep<T extends Pick<Item, 'content_type' | 'author_handle'>>(
  item: T,
  riskNames: readonly string[]
): item is T & {author_handle: string} {
  if (item.content_type !== 'actor' || !item.author_handle) {
    return false;
  }

  const matches = (value: string, against: string) => value.toLowerCase() === against.toLowerCase();

  return riskNames.some((riskName) =>
    SWEEP_TRIGGER_RISKS.some((trigger) => matches(riskName, trigger))
  );
}

// Mirrors FollowerSweepClassifierService.MaxFollowersPerSweep - shown to the analyst before they
// agree, not enforced client-side (the server is the one actually capping it).
const FOLLOWER_SWEEP_CAP = 250;

// Whether this item already has a live (non-removed) submission under this exact vertical - the
// clash the server's SubmissionIdentity unique index would 409 on if the analyst submits again
// without resubmit:true. A 'removed' group never blocks - CreateAsync reopens a removed stream as
// an ordinary revision 1, not a resubmission - so it must not trigger the resubmit prompt either.
// Exported so it can be tested without rendering the dialog, same reasoning as armsFollowerSweep.
export function hasLiveSubmissionIn(
  item: Pick<Item, 'submission_status_groups'>,
  vertical: string
): boolean {
  return item.submission_status_groups.some(
    (group) => group.status !== 'removed' && group.policy_violations.includes(vertical)
  );
}

const STEPS = ['Classify', 'Enrich', 'Confirm'] as const;
type Step = (typeof STEPS)[number];

export type ItemOverride = {
  languages?: string[];
  locations?: string[];
  topics?: string[];
  coreRisks?: string[];
  notes?: string;
  attachments?: string[];
  exampleIds?: string;
};
export type ItemOverrides = Partial<Record<string, ItemOverride>>;

const emptyEnrichment = (
  submissionType: EnrichmentValues['submissionType'] = 'Content'
): EnrichmentValues => ({
  platform: 'youtube',
  submissionType,
  notes: '',
  exampleVideoIds: '',
  languages: [],
  topics: [],
  locations: [],
  coreRisks: []
});

// The Core Risks to hold once `risk` is the selected risk: its one mapped resolver risk when it
// maps to exactly one, otherwise whichever of `current` it still maps to - anything left over from
// a previous selection, or an old submission's pre-resolver value, is not a choice it offers.
export function coreRisksFor(risk: Classification | null, current: string[]): string[] {
  const mapped = risk?.coreRisks ?? [];
  return mapped.length === 1 ? [...mapped] : current.filter((name) => mapped.includes(name));
}

/// The taxonomy nodes an amendment is locked to, resolved from the submission being amended.
/// A deprecated or renamed node has no row to find, so it gets a usable stub: the step-pill badge
/// and submit() only need a name to send.
///
/// Exported for its own test - the stub's `coreRisks` is load-bearing and easy to get wrong. It
/// carries the PRIOR submission's core risks as the stub's mapping, because buildPayload filters
/// the shared core-risk list against groupByVertical's options: a stub claiming no mapping empties
/// that filter and the amendment goes out with no core risks at all, having had them before.
export function resubmitSelections(
  resubmitOf: Submission,
  classifications: Classification[]
): {vertical: Classification; risks: Classification[]} {
  const vertical = classifications.find(
    (c) => isPolicyViolationType(c.type) && c.name === resubmitOf.policy_violation
  ) ?? {
    id: '',
    name: resubmitOf.policy_violation,
    displayName: resubmitOf.policy_violation,
    parentId: null,
    restricted: false,
    type: 'policy_violation' as const,
    category: null,
    categories: ['Content'],
    hiddenFacets: [],
    viewContexts: [],
    isPotentialP0: false
  };

  // One per risk the prior submission carried: amending a two-risk submission must not quietly
  // drop the second.
  const risks = resubmitOf.risks.map(
    (name) =>
      classifications.find(
        (c) => c.type === 'risk' && c.name === name && c.parentId === vertical.id
      ) ?? {
        id: '',
        name,
        displayName: name,
        parentId: vertical.id,
        restricted: false,
        type: 'risk' as const,
        category: resubmitOf.submission_type,
        categories: [resubmitOf.submission_type],
        hiddenFacets: [],
        viewContexts: [],
        isPotentialP0: false,
        coreRisks: resubmitOf.core_risk
      }
  );

  return {vertical, risks};
}

/// One (vertical, leaf risk) the analyst has picked. Flat rather than nested so toggling a leaf is
/// a single filter/append; groupByVertical turns it into the per-submission shape.
export type Selection = {vertical: Classification; risk: Classification};

export type FlaggedItem = {itemId: string; color: FlagColor};

// Flags the items behind the newly created submissions: one PUT /items/flags per colour, split to
// the endpoint's id cap. Keyed by the item the server resolved each classification to (not the
// dialog's own Item.id, which a client-built item only keeps when it is new), and deduped, since an
// item classified under two verticals is one flag. Each request is all-or-nothing server-side, so
// every one is still attempted after another fails - a large batch loses only the chunk that failed.
// Resolves to the first failure once they have all been tried, or undefined when none failed.
export async function applyItemFlags(
  flagged: FlaggedItem[],
  setFlags: (ids: string[], color: FlagColor) => Promise<unknown> = itemsApi.setFlags
): Promise<unknown> {
  const colorByItem = new Map(flagged.map(({itemId, color}) => [itemId, color]));
  const byColor = new Map<FlagColor, string[]>();
  for (const [itemId, color] of colorByItem) {
    byColor.set(color, [...(byColor.get(color) ?? []), itemId]);
  }

  let firstError: unknown;
  for (const [color, itemIds] of byColor) {
    for (let start = 0; start < itemIds.length; start += ITEM_FLAGS_MAX_IDS) {
      try {
        await setFlags(itemIds.slice(start, start + ITEM_FLAGS_MAX_IDS), color);
      } catch (error: unknown) {
        firstError ??= error;
      }
    }
  }

  return firstError;
}

/// One submission's worth of selections: every risk picked under one vertical, in click order, plus
/// the Core Risks they collectively allow. One group = one POST /classifications per item.
export type VerticalGroup = {
  vertical: Classification;
  risks: Classification[];
  coreRiskOptions: string[];
};

// Keyed on vertical.name rather than .id: the resubmitOf stub below is built with id: '' when the
// prior submission's taxonomy node has since been renamed or deprecated, so ids are not unique.
export function groupByVertical(selections: Selection[]): VerticalGroup[] {
  const groups = new Map<string, VerticalGroup>();
  for (const {vertical, risk} of selections) {
    const group = groups.get(vertical.name);
    if (group) {
      group.risks.push(risk);
    } else {
      groups.set(vertical.name, {vertical, risks: [risk], coreRiskOptions: []});
    }
  }

  for (const group of groups.values()) {
    group.coreRiskOptions = [...new Set(group.risks.flatMap((risk) => risk.coreRisks ?? []))];
  }

  return [...groups.values()];
}

// The Core Risks to hold across every selected risk: the union of what each one would hold on its
// own, so a leaf that maps to exactly one still auto-selects it while a leaf that maps to several
// still only keeps what the analyst already chose.
export function coreRisksForAll(risks: Classification[], current: string[]): string[] {
  return [...new Set(risks.flatMap((risk) => coreRisksFor(risk, current)))];
}

// Matches one item's defaultLanguage/defaultAudioLanguage (however that item's data reached us —
// scraped or fetched live, see itemMetadata.ts) to a language picker option. Pulled out of the
// component so it can run per-item in a batch, not just for the batch's primary item.
export function autoSelectLanguageFor(
  item: Item | null | undefined,
  languages: ConfigItem[]
): string | null {
  const code = getAutoSelectedLanguageCode(item?.metadata);
  if (!code) {
    return null;
  }

  const codeOf = (l: ConfigItem) => String(l.metadata?.code ?? '').toLowerCase();
  const wanted = code.toLowerCase();
  // A BCP-47 tag like "en-US" won't match a picker option keyed on the bare "en" code, so fall
  // back to matching just the base subtag before giving up.
  const match =
    languages.find((l) => codeOf(l) === wanted) ??
    languages.find((l) => codeOf(l) === wanted.split(/[-_]/)[0]);
  return match?.name ?? null;
}

// The reference-data pack names countries by their ISO 3166 short official name - "Bolivia,
// Plurinational State of", "United States of America", "Korea, Republic of" - which usually isn't
// how YouTube's About page displays them ("Bolivia", "United States", "South Korea"). The
// comma-inverted ones ("X, Y of") are handled generically by autoSelectLocationFor comparing
// everything before the comma - that alone gets Bolivia, Iran, Moldova, Tanzania and Venezuela for
// free. This table is only for the names that still wouldn't line up after that: plain colloquial
// names with no comma to strip (United States, Turkey, Vietnam), alternate/former names (Swaziland,
// Burma, Macedonia), and the two pairs (the Koreas, the Congos) where the generic comma-strip
// would reduce both members down to the same ambiguous "korea"/"congo" prefix and has to be
// skipped for them - so those four need an explicit code here instead.
// Not exhaustive - a genuinely obscure or unanticipated mismatch still falls through to null.
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  'united states': 'US',
  usa: 'US',
  'united kingdom': 'GB',
  uk: 'GB',
  'south korea': 'KR',
  'north korea': 'KP',
  russia: 'RU',
  vietnam: 'VN',
  laos: 'LA',
  'ivory coast': 'CI',
  micronesia: 'FM',
  brunei: 'BN',
  'cape verde': 'CV',
  'czech republic': 'CZ',
  swaziland: 'SZ',
  macedonia: 'MK',
  burma: 'MM',
  'democratic republic of the congo': 'CD',
  'dr congo': 'CD',
  'republic of the congo': 'CG',
  'congo-brazzaville': 'CG',
  'congo-kinshasa': 'CD',
  palestine: 'PS',
  'vatican city': 'VA',
  vatican: 'VA',
  'east timor': 'TL',
  netherlands: 'NL',
  'the netherlands': 'NL',
  taiwan: 'TW',
  macau: 'MO',
  turkey: 'TR',
  'the bahamas': 'BS',
  'the gambia': 'GM',
  'us virgin islands': 'VI',
  'u.s. virgin islands': 'VI'
};

// Matches one item's country metadata to a location picker option. The two sources of this field
// disagree on format: the live YouTube API path (channels.list snippet.country, via
// YouTubeChannelClient) sends an ISO alpha-2 code ("FR"), matched against a location's
// attrs.countryCode (India: {countryCode: "IN", iso3166_2: "IN"}) - unlike languages, locations
// don't have a "code" field, so this can't reuse the matcher above. The Scrapy path
// (Fields.CHANNEL_LOCATION/COUNTRY, scraped off the channel's About page - see
// crisp_scrapy_common's parse_frontend_channel) instead sends the country's own display name
// ("France", "United States"), which a countryCode match will never hit - handled by three more
// tiers: an exact name match, everything before a comma in the pack's official name (skipped when
// that prefix isn't unique among all locations, e.g. the Koreas/Congos both reducing to the same
// prefix - see COUNTRY_NAME_ALIASES above), then that curated alias table.
export function autoSelectLocationFor(
  item: Item | null | undefined,
  locations: ConfigItem[]
): string | null {
  const raw = getMetaFieldAny(item?.metadata, COUNTRY_FIELD_NAMES);
  const code = raw && raw.toLowerCase() !== 'not specified' ? raw : null;
  if (!code) {
    return null;
  }

  const wanted = code.toLowerCase();
  const countryCodeOf = (l: ConfigItem) => String(l.metadata?.countryCode ?? '').toLowerCase();
  const nameBeforeComma = (l: ConfigItem) => l.name.toLowerCase().split(',')[0]!.trim();

  const byCode = locations.find((l) => countryCodeOf(l) === wanted);
  if (byCode) {
    return byCode.name;
  }

  const byExactName = locations.find((l) => l.name.toLowerCase() === wanted);
  if (byExactName) {
    return byExactName.name;
  }

  const beforeCommaMatches = locations.filter((l) => nameBeforeComma(l) === wanted);
  if (beforeCommaMatches.length === 1) {
    return beforeCommaMatches[0]!.name;
  }

  const aliasCode = COUNTRY_NAME_ALIASES[wanted]?.toLowerCase();
  const byAlias = aliasCode ? locations.find((l) => countryCodeOf(l) === aliasCode) : undefined;
  return byAlias?.name ?? null;
}

export type ItemAutoSelect = {language: string | null; location: string | null};

// Pre-fills a per-item override only for an item whose own auto-select actually differs from the
// primary item's (which the batch-wide field already picked up) - an item with no signal of its
// own still just follows the shared field, same as before this feature existed.
export function seedItemOverrides({
  currentItems,
  primaryItem,
  itemAutoSelects,
  autoSelectedLanguage,
  autoSelectedLocation
}: {
  currentItems: Item[];
  primaryItem: Item | null;
  itemAutoSelects: Map<string, ItemAutoSelect>;
  autoSelectedLanguage: string | null;
  autoSelectedLocation: string | null;
}): ItemOverrides {
  const seededOverrides: ItemOverrides = {};
  for (const item of currentItems) {
    if (item.id === primaryItem?.id) {
      continue;
    }

    const auto = itemAutoSelects.get(item.id);
    const override: ItemOverride = {};
    if (auto?.language && auto.language !== autoSelectedLanguage) {
      override.languages = [auto.language];
    }

    if (auto?.location && auto.location !== autoSelectedLocation) {
      override.locations = [auto.location];
    }

    if (Object.keys(override).length > 0) {
      seededOverrides[item.id] = override;
    }
  }

  return seededOverrides;
}

type Props = {
  readonly items: Item[]; // 1 item = single mode, >1 = batch mode, 0 = closed
  readonly onClose: () => void;
  // Called once with whichever items actually succeeded, fully-formed P0LinkedItem data for each
  // (the resulting submission's stream id, vertical, and submission type), and whether the analyst
  // checked "Create P0 Alert" - so a caller can chain straight into CreateP0AlertDialog without a
  // second round-trip to look any of that up.
  readonly onSubmitted?: (items: Item[], linkedItems: P0LinkedItem[], wantP0: boolean) => void;
  // Set by the single-submission pages: replaces the final Close with "Start a new batch", so an
  // analyst working through a long pasted list can clear it in one click once it is submitted.
  readonly onStartNewBatch?: () => void;
  readonly classifications: Classification[];
  readonly languages: ConfigItem[];
  readonly topics: ConfigItem[];
  readonly locations: ConfigItem[];
  // Set when opened from the moderation queue's review flow, keyed by item id: each classification
  // finishes that item's task (under its lease) and its submission History shows it came from a queue
  // review. It rides on an item's first submission only, since that one completes the task.
  readonly moderationTasks?: Readonly<Record<string, {id: string; leaseId: string}>>;
  // How the analyst reviewed an item, read at submit time and sent with its decision.
  readonly getReview?: (itemId: string) => ReviewSignals | undefined;
  // Sent with each moderation task's classification: why its own policy violation was wrong.
  readonly moderationReason?: string;
  // Called instead of listing an error when a task's lease was lost (409) - nothing was written.
  readonly onLeaseLost?: () => void;
  // The queue task of an item was completed by someone else; the item is skipped here.
  readonly onResolvedElsewhere?: (resolved: ResolvedElsewhere) => void;
  // Which classify flow this dialog instance is embedded in, for the same History "Via" badge -
  // irrelevant (and ignored server-side) when moderationTasks is set.
  readonly origin?: 'investigations' | 'single_submission' | 'image_search';
  // Set when this dialog was opened as an explicit amendment of an existing submission (the
  // Submissions list's Resubmit row action) - single-item only. Locks the vertical/risk to that
  // submission's own (skipping the Classify step), prefills the Enrich step from it, and always
  // sends resubmit: true regardless of whether the item's own submission_status_groups still show
  // a clash (the analyst already told us this is an amendment).
  readonly resubmitOf?: Submission;
  // Flags picked beside Classify on the submission form, keyed by this dialog's item ids, for items
  // that don't exist server-side until they are classified. Deliberately not part of the
  // classification: a flag is a personal bookmark on the item rather than anything the client
  // receives, so it goes through PUT /items/flags once the items exist. Every other caller's items
  // already exist and are flagged directly in their own view, so they pass nothing here.
  readonly flags?: Partial<Record<string, FlagColor>>;
  // Preselects this policy violation (and its risk) when the dialog opens.
  readonly initialPolicyViolation?: string;
  readonly evidenceItemIds?: readonly string[];
};

export function ClassifyDialog({
  items,
  onClose,
  onSubmitted,
  onStartNewBatch,
  classifications,
  languages,
  topics,
  locations,
  moderationTasks,
  getReview,
  moderationReason,
  onLeaseLost,
  onResolvedElsewhere,
  origin,
  resubmitOf,
  flags,
  initialPolicyViolation,
  evidenceItemIds
}: Props) {
  const createClassification = useCreateClassification();
  const existingSubmissionsCheck = useExistingSubmissions();
  const [step, setStep] = useState<Step>('Classify');
  // Every (vertical, leaf risk) the analyst has picked, in click order. One POST per vertical per
  // item - a second risk under the SAME vertical rides on that vertical's one submission, because
  // the dedup key (affiliate|policy_violation|submission_type|subject) cannot separate them.
  const [selections, setSelections] = useState<Selection[]>([]);
  const [enrichment, setEnrichment] = useState<EnrichmentValues>(emptyEnrichment());
  const [submitting, setSubmitting] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [itemOverrides, setItemOverrides] = useState<ItemOverrides>({});
  const [showOverrides, setShowOverrides] = useState(false);
  const [createP0, setCreateP0] = useState(false);
  // Set when the submissions went through but flagging them didn't. Kept apart from `errors`,
  // which are per-submission failures: nothing here needs resubmitting.
  const [flagError, setFlagError] = useState<string | null>(null);
  // Keyed by item AND vertical: the same item can fail under one vertical while succeeding under
  // another, and both need to be nameable.
  const [errors, setErrors] = useState<Array<{item: Item; vertical: string; message: string}>>([]);
  // The prior submission an amendment is prefilled from - either resubmitOf itself (explicit row
  // action) or one found via existingSubmissionsCheck after the analyst picks a vertical the
  // primary item is already live-submitted under (detected path, single-item only). Non-null here
  // is exactly the signal buildPayload uses to send resubmit: true for the primary item.
  const [resubmitSource, setResubmitSource] = useState<Submission | null>(resubmitOf ?? null);
  // Gates the actual submit() call behind ResubmitConfirmDialog whenever there's a prior
  // submission to warn about - set true the moment the analyst hits the Resubmit button, cleared
  // on cancel or once submit() has actually fired.
  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false);
  // True once the analyst has actually edited an Enrich field by hand. The detected-resubmit
  // path's existingSubmissionsCheck probe resolves asynchronously after landing on Enrich - if the
  // analyst starts typing before it lands, its prefill must not clobber what they already typed.
  // Reset to false on every fresh vertical selection (see onSelect below), never inside the
  // resubmitOf effect or setEnrichment calls this file makes itself.
  const enrichmentTouchedRef = useRef(false);
  // Bumped on every ClassificationTree selection, captured by the detected-resubmit probe it may
  // kick off. Two probes can resolve out of order (a fast vertical-B response landing before a
  // slower vertical-A one the analyst has already moved on from), and without this the later
  // resolution would silently overwrite resubmitSource with a DIFFERENT vertical's prior
  // submission - the confirm dialog would then show the wrong submitter/date/notes even though the
  // server-side outcome stays correct (mints an ordinary revision 1, since that prior's BaseKey
  // doesn't match). A probe only applies its result while this still matches the generation it
  // captured at selection time.
  const resubmitProbeGenRef = useRef(0);

  const open = items.length > 0;

  const verticalGroups = useMemo(() => groupByVertical(selections), [selections]);
  const discourageScreenshots = discouragesScreenshots(verticalGroups.map((g) => g.vertical.name));
  // The single-selection view the resubmit, follower-sweep and header-pill paths still want. Every
  // one of them is either single-vertical by construction (see the resubmitOf branch in onSelect)
  // or wants the primary, so reading selections[0] is the right answer rather than a stopgap.
  const selectedVertical = selections[0]?.vertical ?? null;
  const selectedRisk = selections[0]?.risk ?? null;
  const coreRiskFields = useMemo(
    () =>
      verticalGroups
        .filter((group) => group.coreRiskOptions.length > 0)
        .map((group) => ({label: group.vertical.displayName, options: group.coreRiskOptions})),
    [verticalGroups]
  );

  // A batch of items can mix videos and channels/actors. Since a vertical/risk applies to one
  // taxonomy at a time, classify them as separate groups — Content items first, then Actor items —
  // rather than forcing one taxonomy's tree onto both.
  const groups = useMemo(() => {
    const content = items.filter((i) => i.content_type !== 'actor');
    const actor = items.filter((i) => i.content_type === 'actor');
    return [content, actor].filter((g) => g.length > 0);
  }, [items]);
  const [groupIndex, setGroupIndex] = useState(0);
  const [succeededTotal, setSucceededTotal] = useState<Item[]>([]);
  const [linkedItemsTotal, setLinkedItemsTotal] = useState<P0LinkedItem[]>([]);
  // Set once, from the first newly-classified sweep-trigger actor in the batch (see submit()) -
  // offering more than one sweep prompt per classify session is more UI than this needs, since the
  // common case is one analyst identifying one distributor account at a time.
  const [followerSweepCandidate, setFollowerSweepCandidate] = useState<{
    handle: string;
    classificationId: string;
  } | null>(null);
  const navigate = useNavigate();
  const currentItems = useMemo(() => groups[groupIndex] ?? [], [groups, groupIndex]);
  const isLastGroup = groupIndex >= groups.length - 1;

  const isBatch = currentItems.length > 1;
  const primaryItem = currentItems[0] ?? null;
  // An actor (e.g. a channel/account) is never itself "content" — submitting one produces an Actor
  // submission, deduplicated by author_id, instead of a Content submission deduplicated by external_id.
  const defaultSubmissionType: EnrichmentValues['submissionType'] =
    primaryItem?.content_type === 'actor' ? 'Actor' : 'Content';
  // Purely informational - a Potential-P0 risk doesn't change how this submission is submitted (it
  // already goes out immediately either way); it only means a Slack reminder can fire later if the
  // customer hasn't actioned it. See P0EscalationWatch/P0EscalationRunnerService.
  const isPotentialP0Risk = selections.some((selection) => selection.risk.isPotentialP0);
  // A risk that maps to several resolver risks leaves the pick to the analyst, and until they make
  // it the submission would go out without the Core Risk its classification implies.
  // Per vertical: each submission carries its own core_risk[], so one vertical left unanswered
  // blocks Review even when another already has a pick. coreRiskFields is pre-filtered to verticals
  // that map to at least one resolver risk, so a vertical mapping to none is simply not pending.
  const coreRiskPending = coreRiskFields.some(
    (field) => !field.options.some((option) => enrichment.coreRisks.includes(option))
  );
  const {data: escalationConfig} = useP0EscalationConfig();
  const escalationThresholdMinutes = escalationConfig?.threshold_minutes ?? 60;

  // Every current-group item's own auto-selected language/location, computed independently so a
  // mixed batch (e.g. one video in Hindi among mostly-English ones) doesn't get every item tagged
  // with whichever item happens to be first.
  //
  // Keyed on .length rather than the array references themselves: callers default an unloaded
  // config query to a fresh `languages ?? []` / `locations ?? []` on every render, and depending on
  // those references directly would invalidate this memo (and the reset effect that reads it,
  // below) on every such render while a query is still loading - each producing a new object that
  // re-fires state updates. Real reference-data lists don't change shape without a full reload, so
  // .length is a stable enough key without losing correctness.
  const itemAutoSelects = useMemo(() => {
    const map = new Map<string, ItemAutoSelect>();
    for (const item of currentItems) {
      map.set(item.id, {
        language: autoSelectLanguageFor(item, languages),
        location: autoSelectLocationFor(item, locations)
      });
    }

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above re: .length
  }, [currentItems, languages.length, locations.length]);

  const autoSelectedLanguage = primaryItem
    ? (itemAutoSelects.get(primaryItem.id)?.language ?? null)
    : null;
  const autoSelectedLocation = primaryItem
    ? (itemAutoSelects.get(primaryItem.id)?.location ?? null)
    : null;

  // Raw signals behind autoSelectedLanguage, shown alongside the pickers so an analyst can judge
  // the auto-pick against the source metadata rather than trusting it blind.
  const metadataSignals = useMemo(() => {
    const present = (value: string) =>
      value && value.toLowerCase() !== 'not specified' ? value : null;
    return {
      defaultAudioLanguage: present(
        getMetaFieldAny(primaryItem?.metadata, DEFAULT_AUDIO_LANGUAGE_FIELD_NAMES)
      ),
      defaultLanguage: present(
        getMetaFieldAny(primaryItem?.metadata, DEFAULT_LANGUAGE_FIELD_NAMES)
      ),
      country: present(getMetaFieldAny(primaryItem?.metadata, COUNTRY_FIELD_NAMES))
    };
  }, [primaryItem]);

  // Reset group progress whenever the dialog opens with a new set of items.
  useEffect(() => {
    if (!open) {
      return;
    }

    setGroupIndex(0);
    setSucceededTotal([]);
    setLinkedItemsTotal([]);
    setCreateP0(false);
    setFlagError(null);
  }, [open, items.map((i) => i.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset per-group classification state whenever we move to a new group.
  useEffect(() => {
    if (!open) {
      return;
    }

    if (resubmitOf) {
      // Explicit amendment: skip the Classify step entirely and open straight on Enrich, prefilled
      // from the submission being amended.
      const {vertical, risks} = resubmitSelections(resubmitOf, classifications);

      setStep('Enrich');
      setSelections(risks.map((risk) => ({vertical, risk})));
      setEnrichment({
        platform: resubmitOf.platform,
        submissionType: resubmitOf.submission_type,
        notes: resubmitOf.notes ?? '',
        exampleVideoIds: resubmitOf.example_ids ?? '',
        languages: resubmitOf.language,
        topics: resubmitOf.topic,
        locations: resubmitOf.location,
        // No all-stub special case needed: a stub carries the prior's core risks as its own mapping
        // (above), so this is the identity on them.
        coreRisks: coreRisksForAll(risks, resubmitOf.core_risk)
      });
      setResubmitSource(resubmitOf);
      setDoneCount(0);
      setItemOverrides({});
      setShowOverrides(false);
      setErrors([]);
      return;
    }

    setStep('Classify');
    const initialVertical = initialPolicyViolation
      ? classifications.find(
          (classification) =>
            isPolicyViolationType(classification.type) &&
            classification.name === initialPolicyViolation
        )
      : undefined;
    const initialRisk = initialVertical
      ? classifications.find(
          (classification) =>
            classification.type === 'risk' && classification.parentId === initialVertical.id
        )
      : undefined;
    setSelections(
      initialVertical && initialRisk ? [{vertical: initialVertical, risk: initialRisk}] : []
    );
    setEnrichment({
      ...emptyEnrichment(defaultSubmissionType),
      languages: autoSelectedLanguage ? [autoSelectedLanguage] : [],
      locations: autoSelectedLocation ? [autoSelectedLocation] : []
    });
    setResubmitSource(null);
    setDoneCount(0);
    setItemOverrides(
      seedItemOverrides({
        currentItems,
        primaryItem,
        itemAutoSelects,
        autoSelectedLanguage,
        autoSelectedLocation
      })
    );
    setShowOverrides(false);
    setErrors([]);
    // Depends on classifications.length below, not classifications itself: every call site sources
    // this from useClassificationConfig()/a query's .data, several with an inline `?? []`/`= []`
    // fallback - a fresh array reference on every render until the query resolves. Both branches
    // above call setEnrichment/setStep unconditionally, so depending on the array itself re-runs
    // this effect every render while data is loading, which sets new state, which re-renders, which
    // mints another new [] - an infinite render loop for as long as classifications has no data
    // (forever, if that query errors). The length is a stable primitive that still flips once real
    // data arrives, same reasoning as languages.length/locations.length above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    groupIndex,
    defaultSubmissionType,
    autoSelectedLanguage,
    autoSelectedLocation,
    currentItems,
    itemAutoSelects,
    primaryItem,
    resubmitOf,
    initialPolicyViolation,
    classifications.length
  ]);

  // Whether anything currently set — the shared fields or any per-item override — still matches
  // what auto-select put there, i.e. whether "Clear all auto-selects" would actually do anything.
  const hasActiveAutoSelects = useMemo(() => {
    if (
      autoSelectedLanguage &&
      enrichment.languages.length === 1 &&
      enrichment.languages[0] === autoSelectedLanguage
    ) {
      return true;
    }

    if (
      autoSelectedLocation &&
      enrichment.locations.length === 1 &&
      enrichment.locations[0] === autoSelectedLocation
    ) {
      return true;
    }

    return currentItems.some((item) => {
      const auto = itemAutoSelects.get(item.id);
      const override = itemOverrides[item.id];
      if (!auto || !override) {
        return false;
      }

      return (
        (auto.language !== null &&
          override.languages?.length === 1 &&
          override.languages[0] === auto.language) ||
        (auto.location !== null &&
          override.locations?.length === 1 &&
          override.locations[0] === auto.location)
      );
    });
  }, [
    autoSelectedLanguage,
    autoSelectedLocation,
    enrichment.languages,
    enrichment.locations,
    currentItems,
    itemAutoSelects,
    itemOverrides
  ]);

  // Drives the Enrich step's batch-specific banner text: auto-select only ever reads the primary
  // item's metadata, so analysts need to see plainly whether that pick applies to the whole batch
  // or whether some items disagreed and already got their own value (see the reset effect above).
  //
  // Derived from itemAutoSelects, not itemOverrides: an item with no language/location metadata of
  // its own never gets an override (it just silently inherits the batch value, unconfirmed), so
  // counting itemOverrides would render "Matches all N items" for a batch where only the primary
  // item actually has metadata - the opposite of true. Also unlike itemOverrides, this can't be
  // polluted by an override an analyst set by hand on the Confirm step, which isn't a metadata
  // disagreement at all.
  const batchAutoSelectInfo = isBatch
    ? {
        totalItems: currentItems.length,
        languageDeviationCount: currentItems.filter(
          (item) =>
            item.id !== primaryItem?.id &&
            (itemAutoSelects.get(item.id)?.language ?? null) !== autoSelectedLanguage
        ).length,
        locationDeviationCount: currentItems.filter(
          (item) =>
            item.id !== primaryItem?.id &&
            (itemAutoSelects.get(item.id)?.location ?? null) !== autoSelectedLocation
        ).length
      }
    : undefined;

  // Clears only what auto-select actually put in place — the shared Language/Location fields, and
  // any per-item override that still matches that item's own auto-pick — so a genuine manual edit
  // an analyst already made (to a value auto-select never suggested) is left untouched.
  const clearAllAutoSelects = () => {
    setEnrichment((prev) => ({
      ...prev,
      languages:
        autoSelectedLanguage &&
        prev.languages.length === 1 &&
        prev.languages[0] === autoSelectedLanguage
          ? []
          : prev.languages,
      locations:
        autoSelectedLocation &&
        prev.locations.length === 1 &&
        prev.locations[0] === autoSelectedLocation
          ? []
          : prev.locations
    }));

    setItemOverrides((prev) => {
      const next: ItemOverrides = {...prev};
      for (const item of currentItems) {
        const auto = itemAutoSelects.get(item.id);
        const existing = next[item.id];
        if (!auto || !existing) {
          continue;
        }

        const updated: ItemOverride = {...existing};
        if (
          auto.language &&
          updated.languages?.length === 1 &&
          updated.languages[0] === auto.language
        ) {
          delete updated.languages;
        }

        if (
          auto.location &&
          updated.locations?.length === 1 &&
          updated.locations[0] === auto.location
        ) {
          delete updated.locations;
        }

        if (Object.keys(updated).length === 0) {
          Reflect.deleteProperty(next, item.id);
        } else {
          next[item.id] = updated;
        }
      }

      return next;
    });
  };

  const buildPayload = (item: Item, group: VerticalGroup): CreateClassificationRequest => {
    const override = itemOverrides[item.id];
    const itemLanguages = override?.languages ?? enrichment.languages;
    const itemLocations = override?.locations ?? enrichment.locations;
    const itemTopics = override?.topics ?? enrichment.topics;
    // Filtered to this vertical's own mappings: core risks are picked into one shared list, but
    // each submission may only carry the ones its own risks map to. The per-item override only
    // applies while there is a single vertical - it has no vertical dimension to be correct across
    // two (see ItemOverrideRow's coreRiskOptions), so a stale one must not leak into the second.
    const itemCoreRisks = (
      (verticalGroups.length === 1 ? override?.coreRisks : undefined) ?? enrichment.coreRisks
    ).filter((name) => group.coreRiskOptions.includes(name));
    const itemNotes = override?.notes ?? enrichment.notes;

    const enrichmentPayload: NonNullable<CreateClassificationRequest['enrichment']> = {};
    if (itemLanguages.length > 0) {
      enrichmentPayload.language = itemLanguages;
    }

    if (itemTopics.length > 0) {
      enrichmentPayload.topic = itemTopics;
    }

    if (itemLocations.length > 0) {
      enrichmentPayload.location = itemLocations;
    }

    if (itemCoreRisks.length > 0) {
      enrichmentPayload.core_risk = itemCoreRisks;
    }

    // Derived per-item, not from the shared enrichment state — a batch can mix videos and
    // channels/actors, and each must be submitted as its own correct type regardless of what the
    // first item in the batch happened to be.
    const submissionType: CreateClassificationRequest['submission_type'] =
      item.content_type === 'actor' ? 'Actor' : 'Content';

    const taskLease = group === verticalGroups[0] ? moderationTasks?.[item.id] : undefined;
    const review = getReview?.(item.id);
    const body: CreateClassificationRequest = {
      item_id: item.id,
      affiliate: item.affiliate ?? DEFAULT_AFFILIATE,
      policy_violation: group.vertical.name,
      risks: group.risks.map((risk) => risk.name),
      // The item's own real platform wins over the shared enrichment form state - emptyEnrichment's
      // 'youtube' default was previously sent verbatim regardless of the item, which stamped every
      // classification's platform as "youtube" even for a non-YouTube item.
      platform: item.platform || enrichment.platform,
      submission_type: submissionType,
      enrichment: enrichmentPayload,
      ...(item.external_id ? {external_id: item.external_id} : {}),
      ...(item.url ? {url: item.url} : {}),
      ...(item.title ? {title: item.title} : {}),
      ...(item.thumbnail_url ? {thumbnail_url: item.thumbnail_url} : {}),
      ...(item.published_at ? {published_at: item.published_at} : {}),
      ...(item.author_id ? {author_id: item.author_id} : {}),
      ...(item.author_name ? {author_name: item.author_name} : {}),
      ...(item.author_handle ? {author_handle: item.author_handle} : {}),
      // Matters only for an item this classification is about to create - the Single Submission page
      // builds its fields from a live Data API lookup, and they were previously dropped at the API
      // boundary. Harmlessly ignored for an item that already exists.
      ...(item.metadata ? {metadata: item.metadata} : {}),
      ...(taskLease
        ? {
            moderation_task_id: taskLease.id,
            lease_id: taskLease.leaseId,
            ...(review ? {review} : {}),
            ...(moderationReason ? {moderation_reason: moderationReason} : {})
          }
        : {}),
      ...(origin ? {origin} : {})
    };

    if (itemNotes) {
      body.notes = itemNotes;
    }

    // The P0 dialog opens once this lands, so hold delivery until that P0 is approved - otherwise the
    // customer can action the content before the P0 reviewer has validated it.
    if (createP0) {
      body.hold_for_p0 = true;
    }

    const itemAttachments = override?.attachments ?? enrichment.attachments ?? [];
    if (itemAttachments.length > 0) {
      body.attachment_sha256s = itemAttachments;
    }

    // Per-item overridable, since each channel in a batch has its own example videos. A batch can
    // mix actors and content, and example videos only mean anything for the former, so this only
    // ever attaches to an item this batch itself classified as Actor, regardless of what the shared
    // form's "primary item" type was.
    const itemExampleIds = override?.exampleIds ?? enrichment.exampleVideoIds;
    if (submissionType === 'Actor' && (itemExampleIds || evidenceItemIds?.length)) {
      body.example_ids = evidenceItemIds?.length ? evidenceItemIds.join('\n') : itemExampleIds;
    }

    // Only the confirmed single-item paths (explicit resubmitOf, or the detected path once the
    // analyst accepted ResubmitConfirmDialog) opt in. A batch leaves this null, so a clashing item
    // still 409s and surfaces in `errors` rather than silently re-delivering to the client.
    if (resubmitSource !== null) {
      body.resubmit = true;
    }

    return body;
  };

  const submit = async () => {
    if (verticalGroups.length === 0) {
      return;
    }

    setSubmitting(true);
    setErrors([]);
    setFlagError(null);
    try {
      const succeededItems: Item[] = [];
      const linkedItems: P0LinkedItem[] = [];
      const flaggedItems: FlaggedItem[] = [];
      let sweepCandidate: {handle: string; classificationId: string} | null = null;
      // One submission's worth of work, lifted out of the loops: the vertical x item nesting would
      // otherwise put this body four blocks deep inside the try.
      const classifyOne = async (item: Item, group: VerticalGroup) => {
        try {
          const created = await createClassification.mutateAsync(buildPayload(item, group));
          // Deduped by id: an item classified under two verticals succeeded once as far as the
          // caller is concerned, and InvestigationsWorkspace maps each unknown succeeded item into
          // a new row - pushing it twice would render it twice. It also keeps the
          // "{doneCount} of {currentItems.length}" progress line honest.
          if (!succeededItems.some((succeeded) => succeeded.id === item.id)) {
            succeededItems.push(item);
          }

          const flag = flags?.[item.id];
          if (flag) {
            flaggedItems.push({itemId: created.item_id, color: flag});
          }

          // NOT deduped, unlike succeededItems: one stream per vertical is the whole point, and
          // the P0 alert needs every one of them.
          linkedItems.push({
            id: created.submission_stream_id,
            submissionType: item.content_type === 'actor' ? 'Actor' : 'Content',
            policyViolation: group.vertical.name,
            displayName: item.title || item.author_name || item.author_handle || item.external_id,
            externalId: item.external_id,
            handle: item.author_handle
          });

          const arms =
            !sweepCandidate &&
            PLATFORM_ID === 'pinterest' &&
            armsFollowerSweep(
              item,
              group.risks.map((risk) => risk.name)
            );
          if (arms) {
            sweepCandidate = {
              handle: item.author_handle,
              classificationId: created.classification_id
            };
          }
        } catch (error_: unknown) {
          const resolvedElsewhere = resolvedElsewhereOf(error_);
          if (moderationTasks?.[item.id] && group === verticalGroups[0]) {
            if (resolvedElsewhere) {
              onResolvedElsewhere?.(resolvedElsewhere);
              return;
            }

            if (isLeaseLost(error_)) {
              onLeaseLost?.();
              return;
            }
          }

          const fallback = `Failed to classify ${item.title || item.external_id} under ${group.vertical.displayName}`;
          setErrors((prev) => [
            ...prev,
            {item, vertical: group.vertical.name, message: getApiErrorMessage(error_, fallback)}
          ]);
        }
      };

      // Verticals outer, items inner - one POST per (vertical, item). This nests INSIDE one
      // content/actor pass rather than replacing it: `groups` is a separate, sequential walk over a
      // different item set with a different tree category, and succeededTotal/linkedItemsTotal
      // already combine across those passes, so they now combine across verticals for free.
      for (const group of verticalGroups) {
        for (const item of currentItems) {
          await classifyOne(item, group);
        }
      }

      // Before onSubmitted below, which is what tells a caller to reload its grid - the flags must
      // already be there when it does.
      const flagFailure = await applyItemFlags(flaggedItems);
      if (flagFailure !== undefined) {
        setFlagError(
          getApiErrorMessage(
            flagFailure,
            'Submitted, but some flags could not be set - flag the items from the Submissions list'
          )
        );
      }

      setDoneCount(succeededItems.length);
      const combined = [...succeededTotal, ...succeededItems];
      const combinedLinkedItems = [...linkedItemsTotal, ...linkedItems];
      setSucceededTotal(combined);
      setLinkedItemsTotal(combinedLinkedItems);
      if (isLastGroup && combined.length > 0) {
        onSubmitted?.(combined, combinedLinkedItems, createP0);
      }

      if (isLastGroup && sweepCandidate) {
        setFollowerSweepCandidate(sweepCandidate);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const advanceToNextGroup = () => {
    setGroupIndex((i) => i + 1);
  };

  // "SSH › Ideation, Method · GV › Gore" - what was classified, one entry per submission.
  const selectionSummary = verticalGroups
    .map((group) => {
      const riskNames = group.risks.map((risk) => risk.displayName).join(', ');
      return `${group.vertical.displayName} › ${riskNames}`;
    })
    .join(' · ');
  const submissionCountLabel =
    verticalGroups.length > 1 ? ` (${verticalGroups.length} submissions)` : '';
  const p0Label = createP0 ? ' + Create P0 Alert' : '';

  const stepIdx = STEPS.indexOf(step);
  const done = doneCount > 0;

  return (
    <>
      <ResubmitConfirmDialog
        open={showResubmitConfirm}
        source={resubmitSource}
        vertical={selectedVertical?.displayName ?? selectedVertical?.name ?? ''}
        submitting={submitting}
        onCancel={() => {
          setShowResubmitConfirm(false);
        }}
        onConfirm={() => {
          setShowResubmitConfirm(false);
          void submit();
        }}
      />
      <FollowerSweepPromptDialog
        open={followerSweepCandidate !== null}
        handle={followerSweepCandidate?.handle ?? ''}
        classificationId={followerSweepCandidate?.classificationId ?? ''}
        onCancel={() => {
          setFollowerSweepCandidate(null);
        }}
        onStarted={(jobId) => {
          setFollowerSweepCandidate(null);
          // Closed explicitly: the redirect is to the route this dialog is already rendered on, so
          // navigating alone just swaps the ?id= and leaves the classify dialog sitting on top of
          // the sweep job it was meant to reveal.
          onClose();
          void navigate(`/investigations?id=${jobId}`);
        }}
      />
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            onClose();
          }
        }}>
        <DialogContent
          className={cn(
            'transition-[max-width] duration-200',
            step === 'Classify' ? 'sm:max-w-xl' : 'sm:max-w-2xl'
          )}>
          {primaryItem ? (
            <>
              {/* ── Header ── */}
              <DialogHeader>
                <div className="flex items-start gap-3">
                  {isBatch ? (
                    <div className="bg-muted text-muted-foreground flex h-14 w-24 shrink-0 flex-col items-center justify-center gap-0.5">
                      <Film className="size-5" />
                      <span className="text-xs font-medium">{currentItems.length} items</span>
                    </div>
                  ) : primaryItem.thumbnail_url ? (
                    <div className="group bg-muted relative h-14 w-24 shrink-0 overflow-hidden">
                      <SensitiveMedia
                        compact
                        src={primaryItem.thumbnail_url}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <DialogTitle className="line-clamp-2 leading-snug">
                        {isBatch
                          ? `Classifying ${currentItems.length} ${defaultSubmissionType.toLowerCase()} items`
                          : (primaryItem.title ?? primaryItem.external_id)}
                      </DialogTitle>
                      <Badge variant={defaultSubmissionType === 'Actor' ? 'secondary' : 'outline'}>
                        {defaultSubmissionType === 'Actor' ? 'Actor' : 'Content'}
                      </Badge>
                    </div>
                    <DialogDescription className="mt-0.5 flex items-center gap-1.5">
                      {isBatch ? (
                        batchAutoSelectInfo &&
                        (batchAutoSelectInfo.languageDeviationCount > 0 ||
                          batchAutoSelectInfo.locationDeviationCount > 0) ? (
                          'Same vertical & risk for all items — language/location differ for some, see Enrich step'
                        ) : (
                          'The same classification will be applied to all selected items'
                        )
                      ) : (
                        <>
                          {primaryItem.author_handle ?? primaryItem.author_name ?? ''}
                          {primaryItem.url ? (
                            <CopyableUrl url={primaryItem.url} className="ml-1" />
                          ) : null}
                        </>
                      )}
                    </DialogDescription>
                  </div>
                </div>

                {/* Step pills */}
                {done ? null : (
                  <div className="border-border mt-3 flex items-center gap-1.5 border-b pb-3">
                    {STEPS.map((s, i) => (
                      <div key={s} className="flex items-center gap-1">
                        <div
                          aria-hidden="true"
                          className={cn(
                            'flex size-5 items-center justify-center text-xs font-medium',
                            stepIdx >= i
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          )}>
                          {i + 1}
                        </div>
                        <span
                          aria-current={stepIdx === i ? 'step' : undefined}
                          className={cn(
                            'text-xs',
                            stepIdx === i ? 'font-medium' : 'text-muted-foreground'
                          )}>
                          {s}
                        </span>
                        {i < STEPS.length - 1 ? (
                          <span aria-hidden="true" className="text-muted-foreground/50 mx-1">
                            ›
                          </span>
                        ) : null}
                      </div>
                    ))}

                    {selectedVertical && step !== 'Classify' ? (
                      <div className="ml-auto flex items-center gap-1.5">
                        <Badge variant="secondary">{selectedVertical.displayName}</Badge>
                        <span aria-hidden="true" className="text-muted-foreground/50 text-xs">
                          ›
                        </span>
                        <span className="text-xs font-medium">{selectedRisk?.displayName}</span>
                        {selections.length > 1 ? (
                          <span className="text-muted-foreground text-xs">
                            +{selections.length - 1} more
                          </span>
                        ) : null}
                        <Button
                          variant="link"
                          size="xs"
                          className="ml-1 h-auto p-0"
                          onClick={() => {
                            setStep('Classify');
                          }}>
                          Change
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </DialogHeader>

              {items.some((item) => (item.queue_leases ?? []).length > 0) ? (
                <Alert className="mx-5 mt-4 w-auto">
                  <AlertCircle />
                  <AlertTitle>In another analyst&apos;s queue</AlertTitle>
                  <AlertDescription>
                    {items
                      .flatMap((item) => item.queue_leases ?? [])
                      .map((lease) => (
                        <p key={`${lease.owner_name}:${lease.policy_violation}`}>
                          {lease.owner_name} is reviewing this in the {lease.policy_violation}{' '}
                          queue. Classifying here takes it out of their queue.
                        </p>
                      ))}
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* ── Content ── */}
              <DialogBody>
                {/* Error Banner */}
                {errors.length > 0 || flagError ? (
                  <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Some submissions failed</AlertTitle>
                    <AlertDescription>
                      {errors.map((err) => (
                        <p key={`${err.vertical}:${err.item.id}`}>{err.message}</p>
                      ))}
                      {flagError ? <p>{flagError}</p> : null}
                    </AlertDescription>
                  </Alert>
                ) : null}
                {done ? (
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <CheckCircle2 />
                      </EmptyMedia>
                      <EmptyTitle>
                        {isBatch
                          ? `${doneCount} of ${currentItems.length} submitted`
                          : 'Classification submitted'}
                      </EmptyTitle>
                      <EmptyDescription>{selectionSummary}</EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent className="flex-row justify-center gap-2">
                      {isLastGroup ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setDoneCount(0);
                              setStep('Classify');
                              setSelections([]);
                              setEnrichment(emptyEnrichment(defaultSubmissionType));
                            }}>
                            Classify again
                          </Button>
                          {onStartNewBatch ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                onClose();
                                onStartNewBatch();
                              }}>
                              Start a new batch
                            </Button>
                          ) : (
                            <Button size="sm" onClick={onClose}>
                              Close
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button size="sm" onClick={advanceToNextGroup}>
                          Continue to{' '}
                          {groups[groupIndex + 1]?.[0]?.content_type === 'actor'
                            ? 'Actor'
                            : 'Content'}{' '}
                          items →
                        </Button>
                      )}
                    </EmptyContent>
                  </Empty>
                ) : step === 'Classify' ? (
                  <div className="space-y-3">
                    <p className="text-muted-foreground text-xs">
                      Showing risk categories for{' '}
                      <span className="text-foreground font-medium">
                        {defaultSubmissionType === 'Actor' ? 'Actor' : 'Content'}
                      </span>{' '}
                      items. Pick every risk that applies — one submission per policy violation,
                      carrying all the risks you pick under it.
                    </p>
                    {verticalGroups.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {verticalGroups.map((group) =>
                          group.risks.map((risk) => (
                            <Badge
                              key={`${group.vertical.name}:${risk.id || risk.name}`}
                              variant="secondary"
                              className="pr-0.5">
                              <span className="font-medium">{group.vertical.displayName}</span>
                              <span aria-hidden="true" className="opacity-50">
                                ›
                              </span>
                              {risk.displayName}
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`Remove ${risk.displayName}`}
                                className="size-4"
                                onClick={() => {
                                  const next = selections.filter(
                                    (selection) => selection.risk.id !== risk.id
                                  );
                                  setSelections(next);
                                  setEnrichment((prev) => ({
                                    ...prev,
                                    coreRisks: coreRisksForAll(
                                      next.map((selection) => selection.risk),
                                      prev.coreRisks
                                    )
                                  }));
                                }}>
                                <X />
                              </Button>
                            </Badge>
                          ))
                        )}
                      </div>
                    ) : null}
                    <ClassificationTree
                      classifications={classifications}
                      selectedRiskIds={selections.map((selection) => selection.risk.id)}
                      category={defaultSubmissionType}
                      onSelect={(v, r) => {
                        // An amendment is locked to the submission being amended, so a selection
                        // there replaces rather than accumulates - that is what keeps the header's
                        // "Change" escape hatch from producing a second vertical.
                        const next: Selection[] = resubmitOf
                          ? [{vertical: v, risk: r}]
                          : selections.some((selection) => selection.risk.id === r.id)
                            ? selections.filter((selection) => selection.risk.id !== r.id)
                            : [...selections, {vertical: v, risk: r}];
                        const nextRisks = next.map((selection) => selection.risk);
                        setSelections(next);
                        setEnrichment((prev) => ({
                          ...prev,
                          coreRisks: coreRisksForAll(nextRisks, prev.coreRisks)
                        }));
                        setItemOverrides((prev) =>
                          Object.fromEntries(
                            Object.entries(prev).map(([id, override]) => [
                              id,
                              override?.coreRisks
                                ? {
                                    ...override,
                                    coreRisks: coreRisksForAll(nextRisks, override.coreRisks)
                                  }
                                : override
                            ])
                          )
                        );
                        // Deliberately no setStep('Enrich') here: a leaf click toggles a selection
                        // now, and auto-advancing on the first one is exactly what stopped an
                        // analyst adding a second risk. The Classify-step footer button advances.
                        enrichmentTouchedRef.current = false;
                        // Cleared unconditionally, not just in the no-clash else branch below - a
                        // clash on the PREVIOUS vertical must not stay visible while this selection's
                        // own probe (if any) is still in flight.
                        setResubmitSource(null);
                        const probeGen = ++resubmitProbeGenRef.current;

                        // Detected-resubmit path: single item, single vertical only. A batch's items
                        // don't share one prior submission to prefill from, and with more than one
                        // vertical selected there is no single prior to point resubmitSource at -
                        // buildPayload only ever sends resubmit:true for a confirmed one, so a
                        // clashing item still 409s and surfaces in `errors` instead.
                        const singleVertical =
                          new Set(next.map((selection) => selection.vertical.name)).size === 1;
                        if (
                          singleVertical &&
                          !isBatch &&
                          primaryItem &&
                          hasLiveSubmissionIn(primaryItem, v.name)
                        ) {
                          void existingSubmissionsCheck
                            .mutateAsync({
                              platform: primaryItem.platform,
                              externalId: primaryItem.external_id ?? ''
                            })
                            .then((rows) => {
                              if (resubmitProbeGenRef.current !== probeGen) {
                                // A later selection has already superseded this probe - applying its
                                // result now would set resubmitSource for the WRONG vertical/risk.
                                return;
                              }

                              // Excludes is_historic rows deliberately: a historic row backs no live
                              // submission_stream to bump, so ClassificationWriter would create an
                              // ordinary revision 1 for it - resubmitSource must only ever point at a
                              // real prior stream, or the confirm dialog promises a resubmission
                              // number the server won't actually produce. submission_type must match
                              // too: the same vertical can hold a Content row and an Actor row with
                              // different BaseKeys (SubmissionIdentity keys on submission_type), so a
                              // Content prior must never seed resubmitSource for an Actor submit or
                              // vice versa - same wrong-promised-ordinal failure as the historic case.
                              const prior = rows.find(
                                (row) =>
                                  row.policy_violation === v.name &&
                                  row.submission_type === defaultSubmissionType &&
                                  row.action_status !== 'removed' &&
                                  !row.is_historic
                              );
                              if (prior) {
                                // Always sets resubmitSource - that's what makes buildPayload send
                                // resubmit:true and gates ResubmitConfirmDialog; skipping it because
                                // the analyst already typed something would leave this item 409ing.
                                // Only the enrichment PREFILL is conditional: overwriting fields the
                                // analyst has already edited by hand is the actual bug being guarded.
                                setResubmitSource(prior);
                                if (!enrichmentTouchedRef.current) {
                                  setEnrichment((prev) => ({
                                    ...prev,
                                    notes: prior.notes ?? '',
                                    exampleVideoIds: prior.example_ids ?? '',
                                    languages: prior.language,
                                    topics: prior.topic,
                                    locations: prior.location,
                                    coreRisks: coreRisksForAll(nextRisks, prior.core_risk)
                                  }));
                                }
                              }
                            })
                            .catch(() => undefined);
                        }
                      }}
                    />
                  </div>
                ) : step === 'Enrich' && selectedVertical ? (
                  <EnrichmentForm
                    discourageScreenshots={discourageScreenshots}
                    coreRiskFields={coreRiskFields}
                    values={enrichment}
                    languages={languages}
                    topics={topics}
                    locations={locations}
                    autoSelectedLanguage={autoSelectedLanguage}
                    autoSelectedLocation={autoSelectedLocation}
                    channelId={isBatch ? null : primaryItem?.author_id}
                    actorHandle={isBatch ? null : primaryItem?.author_handle}
                    hideExampleIds={isBatch}
                    batchAutoSelectInfo={batchAutoSelectInfo}
                    metadataSignals={metadataSignals}
                    onChange={(values) => {
                      enrichmentTouchedRef.current = true;
                      setEnrichment(values);
                    }}
                  />
                ) : step === 'Confirm' && selectedVertical && selectedRisk ? (
                  <div className="space-y-4">
                    {isBatch ? (
                      <Alert>
                        <AlertTitle>
                          This classification will be applied to{' '}
                          <strong className="font-medium">{currentItems.length} items</strong>
                        </AlertTitle>
                        <AlertDescription>
                          {/* The viewport is `overflow: scroll`, so as a flex item its minimum
                              height is 0 and it shrinks to the cap rather than growing past it. */}
                          <ScrollArea className="flex max-h-20 flex-col">
                            <div className="flex flex-wrap gap-1">
                              {currentItems.map((item) => (
                                <Badge
                                  key={item.id}
                                  variant="outline"
                                  className="max-w-[180px] justify-start">
                                  <span className="truncate">{item.title ?? item.external_id}</span>
                                </Badge>
                              ))}
                            </div>
                          </ScrollArea>
                        </AlertDescription>
                      </Alert>
                    ) : null}
                    {isBatch ? (
                      <Collapsible
                        open={showOverrides}
                        className="border-border block rounded-lg border"
                        onOpenChange={setShowOverrides}>
                        <div className="flex items-center gap-1.5 pr-2">
                          <CollapsibleTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground h-9 flex-1 justify-start px-3"
                              />
                            }>
                            <ChevronRight className="transition-transform in-data-panel-open:rotate-90" />
                            Override details per item
                          </CollapsibleTrigger>
                          {hasActiveAutoSelects ? (
                            <Button
                              variant="link"
                              size="xs"
                              className="text-muted-foreground shrink-0"
                              onClick={clearAllAutoSelects}>
                              Clear all auto-selects
                            </Button>
                          ) : null}
                        </div>
                        <CollapsibleContent>
                          <div className="border-border space-y-2 border-t p-3">
                            {currentItems.map((item) => {
                              const override = itemOverrides[item.id];
                              const setOverride = (patch: Partial<ItemOverride>) => {
                                setItemOverrides((prev) => ({
                                  ...prev,
                                  [item.id]: {...prev[item.id], ...patch}
                                }));
                              };

                              return (
                                <ItemOverrideRow
                                  key={item.id}
                                  item={item}
                                  // Only offered while there is a single vertical: ItemOverride
                                  // keys on item id alone and has no vertical dimension, so it
                                  // cannot express "this item's core risks, under vertical B".
                                  // Empty hides the field via the row's own length check. This
                                  // also fixes the single-vertical two-leaf case, which used to
                                  // offer only the primary leaf's mappings.
                                  coreRiskOptions={
                                    verticalGroups.length === 1
                                      ? verticalGroups[0]!.coreRiskOptions
                                      : []
                                  }
                                  languages={languages}
                                  locations={locations}
                                  topics={topics}
                                  selectedLanguages={override?.languages ?? enrichment.languages}
                                  selectedLocations={override?.locations ?? enrichment.locations}
                                  selectedTopics={override?.topics ?? enrichment.topics}
                                  selectedCoreRisks={override?.coreRisks ?? enrichment.coreRisks}
                                  notes={override?.notes ?? enrichment.notes}
                                  attachments={
                                    override?.attachments ?? enrichment.attachments ?? []
                                  }
                                  exampleIds={override?.exampleIds ?? enrichment.exampleVideoIds}
                                  discourageScreenshots={discourageScreenshots}
                                  onChangeExampleIds={(next) => {
                                    setOverride({exampleIds: next});
                                  }}
                                  onChangeAttachments={(next) => {
                                    setOverride({attachments: next});
                                  }}
                                  onChangeLanguages={(next) => {
                                    setOverride({languages: next});
                                  }}
                                  onChangeLocations={(next) => {
                                    setOverride({locations: next});
                                  }}
                                  onChangeTopics={(next) => {
                                    setOverride({topics: next});
                                  }}
                                  onChangeCoreRisks={(next) => {
                                    setOverride({coreRisks: next});
                                  }}
                                  onChangeNotes={(next) => {
                                    setOverride({notes: next});
                                  }}
                                />
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ) : null}
                    <ItemPanel variant="outline">
                      <ItemContent className="gap-3">
                        {/* One row per vertical: each one is its own submission. */}
                        {verticalGroups.map((group) => {
                          const groupCoreRisks = enrichment.coreRisks.filter((name) =>
                            group.coreRiskOptions.includes(name)
                          );
                          return (
                            <div
                              key={group.vertical.name}
                              className="flex flex-wrap items-center gap-1.5">
                              <Badge>{group.vertical.displayName}</Badge>
                              <span aria-hidden="true" className="text-muted-foreground/50 text-xs">
                                ›
                              </span>
                              {group.risks.map((risk) => (
                                <Badge key={risk.id || risk.name} variant="outline">
                                  {risk.displayName}
                                </Badge>
                              ))}
                              {groupCoreRisks.length > 0 ? (
                                <span className="text-muted-foreground text-xs">
                                  Core Risk: {groupCoreRisks.join(', ')}
                                </span>
                              ) : null}
                              {group.risks.some((risk) => risk.isPotentialP0) ? (
                                <Badge variant="destructive">
                                  <MailWarning />
                                  Potential P0
                                </Badge>
                              ) : null}
                            </div>
                          );
                        })}

                        {[
                          {label: 'Language', values: enrichment.languages},
                          {label: 'Location', values: enrichment.locations},
                          {label: 'Topics', values: enrichment.topics}
                          // Core Risks is deliberately absent: it is per-vertical now, and is shown
                          // on each vertical's own row above rather than as one shared list.
                        ].some((f) => f.values.length > 0) ? (
                          <div className="border-border grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-3">
                            {[
                              {label: 'Language', values: enrichment.languages},
                              {label: 'Location', values: enrichment.locations},
                              {label: 'Topics', values: enrichment.topics}
                            ]
                              .filter((f) => f.values.length > 0)
                              .map((f) => (
                                <div key={f.label} className="min-w-0 space-y-1.5">
                                  <ItemTitle>{f.label}</ItemTitle>
                                  <div className="flex flex-wrap gap-1">
                                    {f.values.map((v) => (
                                      <Badge key={v} variant="outline">
                                        {v}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : null}

                        {enrichment.notes ? (
                          <div className="border-border space-y-1 border-t pt-3">
                            <ItemTitle>Notes</ItemTitle>
                            <p className="text-xs leading-relaxed">{enrichment.notes}</p>
                          </div>
                        ) : null}

                        {enrichment.exampleVideoIds ? (
                          <div className="border-border space-y-1 border-t pt-3">
                            <ItemTitle>Example Video IDs</ItemTitle>
                            <p className="text-xs leading-relaxed whitespace-pre-line">
                              {enrichment.exampleVideoIds}
                            </p>
                          </div>
                        ) : null}
                      </ItemContent>
                    </ItemPanel>

                    {!isBatch && resubmitSource ? (
                      <Alert>
                        <AlertCircle />
                        <AlertTitle>
                          Already submitted under {selectedVertical.displayName}
                        </AlertTitle>
                        {/* Deliberately unnumbered: the server mints the next revision off the
                              highest live one for this whole identity, not off whichever row the
                              analyst happened to open this from, so any number promised here is
                              wrong the moment a second revision already exists. The row badge
                              shows the real ordinal once the submission lands. */}
                        <AlertDescription>
                          Submitting will create a new resubmission, tracked as its own action and
                          delivered to the client again.
                        </AlertDescription>
                      </Alert>
                    ) : null}

                    {isPotentialP0Risk ? (
                      <Alert variant="destructive">
                        <MailWarning />
                        <AlertTitle>Potential P0 escalation risk</AlertTitle>
                        <AlertDescription>
                          {isBatch ? 'These items' : 'This item'} will be{' '}
                          {createP0
                            ? 'held until the P0 is approved'
                            : 'submitted immediately as usual'}
                          . If the customer hasn&apos;t actioned {isBatch ? 'them' : 'it'} within{' '}
                          <strong>{escalationThresholdMinutes} minutes</strong>, a Slack alert will
                          go to the team with a link to create and send the P0 email — nothing to
                          fill in now.
                        </AlertDescription>
                      </Alert>
                    ) : null}

                    {/* The package's choice card: a FieldLabel around a Field edges the card in
                        --foreground while its Checkbox is checked. */}
                    <FieldLabel htmlFor="classify-create-p0">
                      <Field orientation="horizontal">
                        <Checkbox
                          id="classify-create-p0"
                          checked={createP0}
                          onCheckedChange={(checked) => {
                            setCreateP0(checked);
                          }}
                        />
                        <FieldContent>
                          <FieldTitle>
                            <MailWarning className="size-3.5 shrink-0" />
                            Create a P0 Alert for {isBatch ? 'these items' : 'this item'}
                          </FieldTitle>
                          <FieldDescription>
                            {isBatch ? 'They are' : 'It is'} held back from delivery until the P0 is
                            approved, then sent with it.
                          </FieldDescription>
                        </FieldContent>
                      </Field>
                    </FieldLabel>
                  </div>
                ) : null}
              </DialogBody>

              {/* ── Footer ── */}
              {done ? null : (
                <DialogFooter sticky className="sm:justify-between">
                  {step === 'Classify' ? (
                    <span />
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setStep(step === 'Confirm' ? 'Enrich' : 'Classify');
                      }}>
                      <ChevronLeft /> Back
                    </Button>
                  )}
                  {step === 'Classify' ? (
                    <Button
                      size="sm"
                      disabled={selections.length === 0}
                      onClick={() => {
                        setStep('Enrich');
                      }}>
                      {selections.length > 1 ? `Next → (${selections.length} risks)` : 'Next →'}
                    </Button>
                  ) : null}
                  {step === 'Enrich' ? (
                    <Button
                      size="sm"
                      disabled={coreRiskPending}
                      onClick={() => {
                        setStep('Confirm');
                      }}>
                      Review →
                    </Button>
                  ) : null}
                  {step === 'Confirm' ? (
                    <Button
                      size="sm"
                      disabled={submitting}
                      onClick={() => {
                        if (!isBatch && resubmitSource) {
                          setShowResubmitConfirm(true);
                        } else {
                          void submit();
                        }
                      }}>
                      {submitting ? (
                        <>
                          <Spinner />
                          Submitting…
                        </>
                      ) : isBatch ? (
                        `Submit ${currentItems.length} items${submissionCountLabel}${p0Label}`
                      ) : (
                        `${resubmitSource ? 'Resubmit' : 'Submit'}${submissionCountLabel}${p0Label}`
                      )}
                    </Button>
                  ) : null}
                </DialogFooter>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function FollowerSweepPromptDialog({
  open,
  handle,
  classificationId,
  onCancel,
  onStarted
}: {
  readonly open: boolean;
  readonly handle: string;
  readonly classificationId: string;
  readonly onCancel: () => void;
  readonly onStarted: (jobId: string) => void;
}) {
  const profileStatus = usePinterestProfileStatus();
  const createFollowerSweep = useCreateFollowerSweep();
  const [followerCount, setFollowerCount] = useState<number | null>(null);
  const [followerCountUnknown, setFollowerCountUnknown] = useState(false);
  const [bypassLimit, setBypassLimit] = useState(false);

  useEffect(() => {
    if (open && handle) {
      setFollowerCount(null);
      setFollowerCountUnknown(false);
      setBypassLimit(false);
      void profileStatus
        .mutateAsync({username: handle, locale: DEFAULT_PINTEREST_LOCALE})
        .then((status) => {
          setFollowerCount(status.follower_count);
        })
        .catch(() => {
          // The confirm still works without a count to show up front, but the account could just
          // as easily be well over the cap - offer the bypass regardless of an unknown count
          // rather than silently denying it to exactly the accounts most likely to need it.
          setFollowerCountUnknown(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- profileStatus is a fresh mutation object every render
  }, [open, handle]);

  const capped = followerCount !== null && followerCount > FOLLOWER_SWEEP_CAP;
  const offerBypass = capped || followerCountUnknown;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onCancel();
        }
      }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Collect and classify followers?</DialogTitle>
          <DialogDescription>
            Collect @{handle}&apos;s followers and automatically classify each one as a CSAM
            Consumer in a new investigation?
            {followerCount === null ? null : (
              <>
                {' '}
                This account has {followerCount.toLocaleString()} followers
                {capped && !bypassLimit
                  ? `; only the first ${FOLLOWER_SWEEP_CAP} will be classified.`
                  : '.'}
              </>
            )}
            {followerCountUnknown
              ? ` Its follower count couldn't be checked, so it may be over ${FOLLOWER_SWEEP_CAP}.`
              : null}
          </DialogDescription>
        </DialogHeader>
        {offerBypass ? (
          <label className="hover:bg-accent flex cursor-pointer items-center gap-2 px-1.5 py-1 text-xs font-medium">
            <Checkbox checked={bypassLimit} onCheckedChange={setBypassLimit} />
            <span>
              {capped
                ? `Collect all ${followerCount?.toLocaleString()} followers (bypass the ${FOLLOWER_SWEEP_CAP} limit)`
                : `Collect more than ${FOLLOWER_SWEEP_CAP} followers, if there are that many`}
            </span>
          </label>
        ) : null}
        {createFollowerSweep.error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>{getApiErrorMessage(createFollowerSweep.error)}</AlertTitle>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            disabled={createFollowerSweep.isPending}
            onClick={onCancel}>
            No
          </Button>
          <Button
            size="sm"
            disabled={createFollowerSweep.isPending}
            onClick={() => {
              createFollowerSweep.mutate(
                {
                  source_classification_id: classificationId,
                  ...(bypassLimit ? {bypass_follower_limit: true} : {})
                },
                {
                  onSuccess(job) {
                    onStarted(job.id);
                  }
                }
              );
            }}>
            {createFollowerSweep.isPending ? (
              <>
                <Spinner />
                Starting…
              </>
            ) : (
              'Yes, collect followers'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResubmitConfirmDialog({
  open,
  source,
  vertical,
  submitting,
  onCancel,
  onConfirm
}: {
  readonly open: boolean;
  // Null for one render: the dialog closes (via onCancel's state flip) on the same tick submit()
  // clears resubmitSource on the batch/single-item reset paths, so this can't assume non-null just
  // because `open` was recently true.
  readonly source: Submission | null;
  readonly vertical: string;
  readonly submitting: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  if (!source) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onCancel();
        }
      }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Resubmit under {vertical}?</DialogTitle>
          <DialogDescription>
            This item was already submitted by <strong>{source.created_by}</strong> on{' '}
            <strong>{new Date(source.created_at).toLocaleString()}</strong>. Submitting now creates
            a new resubmission, tracked as its own action and delivered to the client again - the
            original submission is left as-is.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted/40 border-border space-y-1.5 rounded-lg border px-3.5 py-3 text-xs">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">
              {source.risks.length > 1 ? 'Previous risks' : 'Previous risk'}
            </span>
            <span className="text-right font-medium">{source.risks.join(', ') || '—'}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Status</span>
            <span className="text-right font-medium capitalize">{source.action_status}</span>
          </div>
          {source.delivered_at ? (
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Delivered to client</span>
              <span className="text-right font-medium">
                {new Date(source.delivered_at).toLocaleString()}
              </span>
            </div>
          ) : null}
          {source.notes ? (
            <div className="border-border/60 border-t pt-1.5">
              <p className="text-muted-foreground mb-0.5">Previous notes</p>
              <p className="leading-relaxed">{source.notes}</p>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={submitting} onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={submitting} onClick={onConfirm}>
            {submitting ? (
              <>
                <Spinner />
                Resubmitting…
              </>
            ) : (
              'Yes, resubmit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemOverrideRow({
  item,
  coreRiskOptions,
  languages,
  locations,
  topics,
  selectedLanguages,
  selectedLocations,
  selectedTopics,
  selectedCoreRisks,
  notes,
  attachments,
  exampleIds,
  discourageScreenshots,
  onChangeExampleIds,
  onChangeAttachments,
  onChangeLanguages,
  onChangeLocations,
  onChangeTopics,
  onChangeCoreRisks,
  onChangeNotes
}: {
  readonly item: Item;
  // The only Core Risks this item may carry - empty hides the field entirely, which is what a
  // multi-vertical selection passes (see the call site for why).
  readonly coreRiskOptions: string[];
  readonly languages: ConfigItem[];
  readonly locations: ConfigItem[];
  readonly topics: ConfigItem[];
  readonly selectedLanguages: string[];
  readonly selectedLocations: string[];
  readonly selectedTopics: string[];
  readonly selectedCoreRisks: string[];
  readonly notes: string;
  readonly attachments: string[];
  readonly exampleIds: string;
  readonly discourageScreenshots: boolean;
  readonly onChangeExampleIds: (exampleIds: string) => void;
  readonly onChangeAttachments: (shas: string[]) => void;
  readonly onChangeLanguages: (languages: string[]) => void;
  readonly onChangeLocations: (locations: string[]) => void;
  readonly onChangeTopics: (topics: string[]) => void;
  readonly onChangeCoreRisks: (coreRisks: string[]) => void;
  readonly onChangeNotes: (notes: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');
  const [coreRiskSearch, setCoreRiskSearch] = useState('');

  const filteredTopics = topicSearch
    ? topics.filter((t) => t.name.toLowerCase().includes(topicSearch.toLowerCase()))
    : topics;

  const relevantCoreRisks = useMemo(
    () => coreRiskOptions.map((name) => ({id: name, name})),
    [coreRiskOptions]
  );
  const filteredCoreRisks = coreRiskSearch
    ? relevantCoreRisks.filter((cr) => cr.name.toLowerCase().includes(coreRiskSearch.toLowerCase()))
    : relevantCoreRisks;

  const toggleValue = (list: string[], name: string) =>
    list.includes(name) ? list.filter((x) => x !== name) : [...list, name];
  const summary =
    [selectedLanguages.join(', '), selectedLocations.join(', '), selectedTopics.join(', ')]
      .filter(Boolean)
      .join(' · ') || 'Using batch default';

  return (
    <div className="border-border relative rounded-lg border">
      <Button
        variant="ghost"
        aria-expanded={expanded}
        className="h-auto w-full justify-start gap-2 px-3 py-2 font-normal"
        onClick={() => {
          setExpanded((e) => !e);
        }}>
        {item.thumbnail_url ? <span className="h-8 w-12 shrink-0" /> : null}
        <span className="min-w-0 flex-1 truncate text-left text-xs font-medium">
          {item.title ?? item.external_id}
        </span>
        <span className="text-muted-foreground max-w-[140px] shrink-0 truncate text-xs">
          {summary}
        </span>
        <ChevronRight
          className={cn(
            'text-muted-foreground shrink-0 transition-transform',
            expanded && 'rotate-90'
          )}
        />
      </Button>
      {item.thumbnail_url ? (
        // Over the spacer in the button above, not inside it: a button can't nest the blur toggle.
        <div className="group absolute top-2 left-3 h-8 w-12">
          <SensitiveMedia
            compact
            src={item.thumbnail_url}
            alt=""
            className="bg-muted size-full object-cover"
            onClick={() => {
              setExpanded((e) => !e);
            }}
          />
        </div>
      ) : null}
      {expanded ? (
        <div className="border-border space-y-3 border-t p-3">
          {languages.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-xs font-medium">Language</p>
              <LanguagePicker
                languages={languages}
                selected={selectedLanguages}
                onToggle={(name) => {
                  onChangeLanguages(toggleValue(selectedLanguages, name));
                }}
              />
            </div>
          ) : null}
          {locations.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-xs font-medium">Location</p>
              <LocationPicker
                locations={locations}
                selected={selectedLocations}
                search={locationSearch}
                onToggle={(name) => {
                  onChangeLocations(toggleValue(selectedLocations, name));
                }}
                onSearch={setLocationSearch}
              />
            </div>
          ) : null}
          {topics.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-xs font-medium">Topics</p>
              <MultiPicker
                items={filteredTopics}
                selected={selectedTopics}
                search={topicSearch}
                placeholder="Search topics…"
                onToggle={(name) => {
                  onChangeTopics(toggleValue(selectedTopics, name));
                }}
                onSearch={setTopicSearch}
              />
            </div>
          ) : null}
          {relevantCoreRisks.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-xs font-medium">Core Risk</p>
              <MultiPicker
                items={filteredCoreRisks}
                selected={selectedCoreRisks}
                search={coreRiskSearch}
                placeholder="Search core risks…"
                onToggle={(name) => {
                  onChangeCoreRisks(toggleValue(selectedCoreRisks, name));
                }}
                onSearch={setCoreRiskSearch}
              />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">Notes</p>
            <NoteScreenshots
              shas={attachments}
              discouraged={discourageScreenshots}
              onChange={onChangeAttachments}>
              <Textarea
                value={notes}
                rows={2}
                aria-label="Notes"
                className="min-h-0 resize-none"
                placeholder="Context, observations, URL list…"
                onChange={(e) => {
                  onChangeNotes(e.target.value);
                }}
              />
            </NoteScreenshots>
          </div>
          {item.content_type === 'actor' ? (
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-xs font-medium">{exampleIdsLabel}</p>
              <ExampleIdsInput
                channelId={item.author_id}
                actorHandle={item.author_handle}
                value={exampleIds}
                onChange={onChangeExampleIds}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
